// Live rewards for signed-in learners: “+100 XP” corner pop-ups, a level-up banner, and the
// one-time medal pop-up when the Support Desk or a whole domain is finished.
// Presentation only — everything is recomputed from the verified completion records
// (src/core/medals.js). The only thing written is `awards-seen` (which medal pop-ups were shown).
import { medalStatus, learnerXp, rewardDiff, SEEN_KEY } from '../core/medals.js';
import { awardModal, rewardToast } from './medals-view.js';

/**
 * @param {object} o
 * @param {object} o.store       learner store (nell:v1:)
 * @param {object} o.progress    learner progress
 * @param {object} o.support     learner Support Desk progress
 * @param {object} o.curriculum
 * @param {() => (string|null)} o.learnerId  id of the signed-in learner, or null (signed out / admin)
 * @param {() => void} o.openCabinet         navigate to the Profile medal cabinet
 */
export function createRewards({ store, progress, support, curriculum, learnerId, openCabinet }) {
  let base = null; // { user, xp, level, medals: [] }
  let timer = null;
  let modal = null;
  let scrollToCabinet = false;
  // automated tests run with window.__NELL_FAST__ unless they ask for rewards explicitly
  const enabled = () => typeof window !== 'undefined' && (!window.__NELL_FAST__ || !!window.__RZ_REWARDS__);

  const snapshot = (user) => {
    const status = medalStatus({ progress, curriculum, support });
    return { user, ...learnerXp({ progress, curriculum, support }), medals: status.filter((m) => m.earned).map((m) => m.id), status };
  };
  const seen = () => { const s = store.get(SEEN_KEY, []); return Array.isArray(s) ? s : []; };
  const markSeen = (ids) => store.set(SEEN_KEY, [...new Set([...seen(), ...ids])]);
  const nextFor = (medal, status) => {
    const i = status.findIndex((m) => m.id === medal.id);
    const n = status.slice(i + 1).find((m) => !m.earned);
    return n ? `${n.title} — the ${n.name}` : null;
  };
  const showMedal = (ids, status) => {
    if (!ids.length || modal) return;
    const earned = status.filter((m) => ids.includes(m.id)).sort((a, b) => (b.earnedAt || 0) - (a.earnedAt || 0));
    markSeen(ids);
    const medal = earned[0];
    modal = awardModal(medal, {
      more: earned.length - 1,
      next: nextFor(medal, status),
      onCabinet: () => { scrollToCabinet = true; openCabinet(); },
      onClose: () => { modal = null; },
    });
  };
  const updateNav = (now, bump = true) => {
    const nav = document.querySelector('[data-testid="nav-xp"]');
    if (!nav) return;
    nav.textContent = `LV ${String(now.level).padStart(2, '0')} · ${now.xp.toLocaleString()} XP`;
    if (bump) { nav.classList.remove('rz-xp-bump'); void nav.offsetWidth; nav.classList.add('rz-xp-bump'); }
  };

  /** Compare with the last snapshot and celebrate what is new. */
  function check() {
    timer = null;
    const user = learnerId();
    if (!user || !base || base.user !== user) return;
    const now = snapshot(user);
    // server-verified XP can arrive after the page was drawn: keep the nav total right even when
    // pop-ups are off
    if (!enabled()) { base = now; updateNav(now, false); return; }
    const diff = rewardDiff(base, now);
    base = now;
    if (diff.xp) { rewardToast({ xp: diff.xp }); updateNav(now); }
    if (diff.levelUp) rewardToast({ level: diff.levelUp });
    const fresh = diff.medals.filter((id) => !seen().includes(id));
    if (fresh.length) showMedal(fresh, now.status);
  }

  return {
    /** After any learner-store write (debounced, so one completion = one check). */
    schedule() { if (base && !timer) timer = setTimeout(check, 250); },
    /**
     * Called on every page render. A different learner (sign-in, boot, account switch) starts a
     * fresh baseline: nothing already earned is announced as new XP; a medal earned earlier whose
     * pop-up was never shown is shown once.
     */
    sync() {
      const user = learnerId();
      if (!user) { base = null; return; }
      if (base?.user === user) return;
      clearTimeout(timer); timer = null;
      base = snapshot(user);
      if (!enabled()) return;
      const unseen = base.medals.filter((id) => !seen().includes(id));
      if (unseen.length) setTimeout(() => { if (learnerId() === user) showMedal(unseen, base.status); }, 400);
    },
    /** Start a fresh baseline for the current learner now (e.g. after server-verified progress loaded). */
    rebase() {
      base = null; this.sync();
      // the verified lists arrive after the page was drawn: show the right XP without a pop-up
      if (base) updateNav(base, false);
    },
    /** True once after “See my medals”, so the Profile page scrolls to the cabinet. */
    takeCabinetScroll() { const v = scrollToCabinet; scrollToCabinet = false; return v; },
    check,
  };
}
